import type { Permission, Session } from "@backchannel/tax-contracts";

/**
 * Three distinct negative states, never collapsed into one grey button.
 *
 *  - `denied`      — about *you*: your role lacks the permission. Recoverable
 *                    only by an administrator. We name the exact permission.
 *  - `disabled`    — about the *record*: the action is legitimate for you but
 *                    not valid in the current state (already published, no
 *                    second approver yet, nothing selected).
 *  - `unavailable` — about the *platform*: a dependency is down or the feature
 *                    is not configured in this environment. Retry later.
 *
 * Precedence is deliberate: `denied` outranks `unavailable`, which outranks
 * `disabled`. Telling an operator "service unavailable" for something they
 * could never do is a false lead; telling them "not yet" when they are not
 * allowed at all wastes their time.
 */
export type AccessState =
  | { kind: "allowed" }
  | { kind: "denied"; permission: Permission }
  | { kind: "disabled"; reason: string }
  | { kind: "unavailable"; reason: string };

export type AccessInput = {
  permissions: readonly Permission[];
  /** All must be held. Empty means no permission gate. */
  required?: readonly Permission[];
  /** Non-null makes the action `disabled` with this explanation. */
  disabledReason?: string | null;
  /** Non-null makes the action `unavailable` with this explanation. */
  unavailableReason?: string | null;
};

export const ADMIN_PERMISSION: Permission = "ADMIN:TAX_PLATFORM:ALL";

export function hasPermission(permissions: readonly Permission[], permission: Permission): boolean {
  return permissions.includes(permission) || permissions.includes(ADMIN_PERMISSION);
}

export function hasAllPermissions(
  permissions: readonly Permission[],
  required: readonly Permission[],
): boolean {
  return required.every((permission) => hasPermission(permissions, permission));
}

export function missingPermission(
  permissions: readonly Permission[],
  required: readonly Permission[],
): Permission | null {
  return required.find((permission) => !hasPermission(permissions, permission)) ?? null;
}

export function evaluateAccess(input: AccessInput): AccessState {
  const missing = missingPermission(input.permissions, input.required ?? []);
  if (missing) {
    return { kind: "denied", permission: missing };
  }
  if (input.unavailableReason) {
    return { kind: "unavailable", reason: input.unavailableReason };
  }
  if (input.disabledReason) {
    return { kind: "disabled", reason: input.disabledReason };
  }
  return { kind: "allowed" };
}

export function isAllowed(state: AccessState): state is { kind: "allowed" } {
  return state.kind === "allowed";
}

/**
 * Approval progress. The author may approve and publish. A principal still
 * records only one decision per proposal.
 */
export function evaluateFourEyes(input: {
  session: Pick<Session, "principalId">;
  authorPrincipalId: string;
  approvals: ReadonlyArray<{ principalId: string; decision: "APPROVED" | "REJECTED" }>;
  requiredApprovals: number;
}): {
  isAuthor: boolean;
  alreadyDecided: boolean;
  approvedCount: number;
  satisfied: boolean;
  blockedReasonKey: "self" | "duplicate" | null;
} {
  const isAuthor = input.session.principalId === input.authorPrincipalId;
  const alreadyDecided = input.approvals.some(
    (approval) => approval.principalId === input.session.principalId,
  );
  const approvedCount = input.approvals.filter(
    (approval) => approval.decision === "APPROVED",
  ).length;

  return {
    isAuthor,
    alreadyDecided,
    approvedCount,
    satisfied: approvedCount >= input.requiredApprovals,
    blockedReasonKey: alreadyDecided ? "duplicate" : null,
  };
}
